import './lib/frontDoor/siteFragment'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister'
import App from './App.tsx'
import { queryClient } from './lib/queryClient'

// Persist the react-query cache to localStorage so the dashboard list renders
// instantly on reload (the snapshot), then revalidates in the background.
// Only conversation queries are persisted; bump `buster` if their shape changes.
const persister = createSyncStoragePersister({
  storage: window.localStorage,
  key: 'ivy-rq-cache',
})
import './index.css'
import { workerSecurityService } from './services/workerSecurityService'
import { workerGA4 } from '../analytics/workerGA4Analytics'
import { workerClarity } from './analytics/clarityAnalytics'
import { initConsentGate } from './analytics/consentGate'
import { startAnalytics } from './analytics/startAnalytics'
import './utils/clearOldModels' // Auto-validates and fixes old model settings
import { initializeTheme } from './hooks/useTheme'
import { startStudioB } from './lib/studioB/flag'
import { productionUploadClient } from './lib/footage/productionUploadClient'
import { registerFootageUploadClient } from './lib/footage/uploadClient'
import { adoptSiteDraftFromUrl } from './lib/frontDoor/siteDraft'

// Consent first: GA4 and Clarity load only when the visitor's consent state allows
// it (4k_consent shared with 4klabs.ai, GPC, region) — see src/analytics/consent.ts.
initConsentGate();

// Initialize theme before render to prevent flash
initializeTheme();

// Direction B (beta builds only): its tokens and <html data-ui="b">. Off, this loads nothing.
startStudioB();

// Extract and store JWT token from URL FIRST if present
const urlParams = new URLSearchParams(window.location.search);
const token = urlParams.get('token');
const userId = urlParams.get('userId') || localStorage.getItem('userId');

if (token) {
  // Store token for API calls
  localStorage.setItem('bearer_token', token);

  // Clean URL to hide token
  const cleanUrl = window.location.pathname + window.location.hash;
  window.history.replaceState({}, '', cleanUrl);

  console.log('JWT token extracted and stored');
}

if (userId) {
  localStorage.setItem('userId', userId);
}

startAnalytics(userId);

// NOW validate worker access after token is potentially stored
workerSecurityService.validateWorkerAccess().then(isAuthorized => {
  if (!isAuthorized) {
    console.error('Worker access denied - redirecting...');
    workerGA4.trackEvent('worker_access_denied', {
      referrer: document.referrer,
      page: window.location.pathname
    });
    // The service will handle the redirect
    return;
  } else {
    workerGA4.trackEvent('worker_access_granted', {
      userId: userId || 'anonymous',
      page: window.location.pathname
    });

    // Track with Clarity
    workerClarity.trackEvent('worker_access_granted', {
      userId: userId || 'anonymous'
    });
  }
});

// Register PWA service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

registerFootageUploadClient(productionUploadClient);

adoptSiteDraftFromUrl();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 24 * 60 * 60 * 1000,
        buster: 'ivy-conv-v1',
        dehydrateOptions: {
          shouldDehydrateQuery: (q) =>
            Array.isArray(q.queryKey) && q.queryKey[0] === 'conversations',
        },
      }}
    >
      <App />
    </PersistQueryClientProvider>
  </React.StrictMode>,
)